/**
 * Everything customer-facing happens in Europe/London (CLAUDE.md §7).
 * These helpers convert between UTC instants and London wall-clock time
 * without a timezone library, using Intl (available in Workers, browsers and Node).
 */
export const LONDON = 'Europe/London';

export interface WallClock {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number;
  minute: number;
  second: number;
  /** 0 = Sunday */
  weekday: number;
  /** 'YYYY-MM-DD' */
  dateKey: string;
  /** minutes since midnight */
  minutes: number;
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function fmt(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short' });
    fmtCache.set(tz, f);
  }
  return f;
}
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function wallClock(d: Date, tz = LONDON): WallClock {
  const parts: Record<string, string> = {};
  for (const p of fmt(tz).formatToParts(d)) parts[p.type] = p.value;
  const year = Number(parts.year), month = Number(parts.month), day = Number(parts.day);
  const hour = Number(parts.hour) % 24, minute = Number(parts.minute), second = Number(parts.second);
  const weekday = WEEKDAYS.indexOf(parts.weekday ?? 'Sun');
  const pad = (n: number) => String(n).padStart(2, '0');
  return { year, month, day, hour, minute, second, weekday, dateKey: `${year}-${pad(month)}-${pad(day)}`, minutes: hour * 60 + minute };
}

/** The UTC instant for a London wall time. Handles BST/GMT. */
export function fromWallClock(dateKey: string, hour: number, minute: number, tz = LONDON): Date {
  const [y, m, d] = dateKey.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d, hour, minute, 0);
  // offset = wall(guess) - guess, then correct
  const w = wallClock(new Date(guess), tz);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  const offset = asUtc - guess;
  return new Date(guess - offset);
}

/** 'YYYY-MM-DD' + n days (calendar arithmetic, timezone-safe). */
export function addDays(dateKey: string, n: number): string {
  const [y, m, d] = dateKey.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

export function hhmm(d: Date, tz = LONDON): string {
  const w = wallClock(d, tz);
  return `${String(w.hour).padStart(2, '0')}:${String(w.minute).padStart(2, '0')}`;
}

export function parseHHMM(s: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) throw new RangeError(`bad time "${s}"`);
  return Number(m[1]) * 60 + Number(m[2]);
}

export function longDate(d: Date, tz = LONDON): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'long', day: 'numeric', month: 'long' }).format(d);
}
export function shortDate(d: Date, tz = LONDON): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short' }).format(d);
}
